import {useRef, useState} from 'react';
import {FileWithPath} from 'react-dropzone';
import {Controller, FormProvider, useForm, useWatch} from 'react-hook-form';
import {CheckboxElement, TextFieldElement} from 'react-hook-form-mui';
import {yupResolver} from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  Chip,
  Grid2 as Grid,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import useUpdateEffect from 'ahooks/es/useUpdateEffect';
import {isAxiosError} from 'axios';
import isNil from 'lodash/isNil';
import {useSnackbar} from 'notistack';
import {boolean, mixed, object, string} from 'yup';

import {
  Category,
  CategorySelect,
  SubCategory,
  SubCategorySelect,
} from '@entities/category';
import {
  GeoFilterOption,
  useGetGeoFilterOptions,
  useUploadAdminFile,
} from '@entities/common';
import {useCreateTopic} from '@entities/topic';
import {getErrorMessageFromApiError} from '@shared/api';
import {
  imageFileTypes,
  ImageUploadField,
  ObjectSelect,
  RhfDateTimePicker,
  TranslateFieldButton,
} from '@shared/components';
import {resolveEnglishText} from '@shared/helpers';
import {SupportedLanguage, TranslationDict} from '@shared/types';
import {dummyFunc} from '@shared/utils';
import {FormRow} from '@widgets/common';
import {openAddTranslationsDialog} from '@widgets/common/AddTranslationsDialog';

type FormValues = {
  slug: string;
  name: string;
  shortName: string;
  category: Category | null;
  subCategory: SubCategory | null;
  geos: GeoFilterOption[];
  logoFile: FileWithPath | null;
  bannerFile: FileWithPath | null;
  startDate: Date | null;
  endDate: Date | null;
  autoPublishEnabled: boolean;
};

const v8nSchema = object().shape({
  slug: string().required(),
  name: string().required(),
  shortName: string().required(),
  category: mixed<Category>()
    .required()
    .nullable()
    .notOneOf([null], 'required'),
  subCategory: mixed<SubCategory>().nullable().defined(),
  geos: mixed<GeoFilterOption[]>().defined().default([]),
  logoFile: mixed<FileWithPath>().nullable().defined(),
  bannerFile: mixed<FileWithPath>().nullable().defined(),
  startDate: mixed<Date>().nullable().defined(),
  endDate: mixed<Date>().nullable().defined(),
  autoPublishEnabled: boolean().required(),
});

interface Props {
  onCreated: () => void;
  onCancel: () => void;
}

export const CreateTopicForm = ({onCreated, onCancel}: Props) => {
  const {enqueueSnackbar} = useSnackbar();
  const {mutateAsync: uploadAdminFile, isPending: isUploadingFiles} =
    useUploadAdminFile();
  const {mutateAsync: createTopic, isPending: isCreatingTopic} =
    useCreateTopic();
  const {data: geoOptions = []} = useGetGeoFilterOptions();
  const [nameTranslations, setNameTranslations] = useState<TranslationDict>(
    {} as TranslationDict,
  );

  const formMethods = useForm<FormValues>({
    mode: 'onBlur',
    resolver: yupResolver(v8nSchema),
    defaultValues: {
      slug: '',
      name: '',
      shortName: '',
      category: null,
      subCategory: null,
      geos: [],
      logoFile: null,
      bannerFile: null,
      startDate: null,
      endDate: null,
      autoPublishEnabled: false,
    },
  });
  const {
    control,
    handleSubmit,
    setValue,
    formState: {dirtyFields},
  } = formMethods;

  const currentName = useWatch({control, name: 'name'});
  const currentCategory = useWatch({control, name: 'category'});
  const prevCategoryId = useRef<string | undefined>();

  useUpdateEffect(() => {
    if (
      !isNil(currentCategory) &&
      currentCategory.id !== prevCategoryId.current
    ) {
      setValue('subCategory', null);
    }
    prevCategoryId.current = currentCategory?.id;
  }, [currentCategory?.id]);

  const onSubmit = async (values: FormValues) => {
    try {
      const uploadedLogo = values.logoFile
        ? await uploadAdminFile({file: values.logoFile, type: 'topic_logo'})
        : null;

      const uploadedBanner = values.bannerFile
        ? await uploadAdminFile({file: values.bannerFile, type: 'topic_banner'})
        : null;

      await createTopic({
        slug: values.slug,
        nameTrans: {
          ...nameTranslations,
          [SupportedLanguage.English]: resolveEnglishText(
            nameTranslations,
            values.name,
            !!dirtyFields.name,
          ),
        },
        shortName: values.shortName,
        logoUrl: uploadedLogo?.key ?? '',
        bannerUrl: uploadedBanner?.key ?? '',
        categorySlug: values.category?.id ?? undefined,
        subcategorySlug:
          values.subCategory?.slug ?? values.subCategory?.id ?? undefined,
        startDate: values.startDate,
        endDate: values.endDate,
        geoSlugs: values.geos.map(g => g.id),
        autoPublishEnabled: values.autoPublishEnabled,
      });

      enqueueSnackbar('Topic created successfully', {variant: 'success'});
      onCreated();
    } catch (error) {
      const errorMessage = isAxiosError(error)
        ? getErrorMessageFromApiError(error)
        : '';

      enqueueSnackbar(`Could not create topic. ${errorMessage}`, {
        variant: 'error',
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <FormProvider {...formMethods}>
        <Grid container rowSpacing={1} columnSpacing={4} mt={2}>
          <Grid size={6}>
            <Stack spacing={5} mb={4}>
              <TextFieldElement
                name="slug"
                label="Slug"
                control={control}
                autoFocus
              />

              <Box display="flex" alignItems="center" gap={2}>
                <TextFieldElement
                  name="name"
                  label="Name"
                  control={control}
                  sx={{flex: 1}}
                />

                <TranslateFieldButton
                  onClick={() => {
                    void openAddTranslationsDialog({
                      fieldName: 'Name',
                      initialTranslations: nameTranslations,
                      enTrans: resolveEnglishText(
                        nameTranslations,
                        currentName,
                        !!dirtyFields.name,
                      ),
                    })
                      .then((translations: TranslationDict) => {
                        setValue(
                          'name',
                          translations[SupportedLanguage.English],
                        );
                        setNameTranslations(translations);
                        return;
                      })
                      .catch(dummyFunc);
                  }}
                />
              </Box>

              <TextFieldElement
                name="shortName"
                label="Short name"
                control={control}
              />
              <FormRow>
                <RhfDateTimePicker
                  name="startDate"
                  control={control}
                  label="Start date"
                />
                <RhfDateTimePicker
                  name="endDate"
                  control={control}
                  label="End date"
                />
              </FormRow>
              <CheckboxElement
                name="autoPublishEnabled"
                label="Auto publish"
                control={control}
              />
            </Stack>
          </Grid>

          <Grid size={6}>
            <Stack spacing={5}>
              <Controller
                name="category"
                control={control}
                render={({field, fieldState}) => (
                  <CategorySelect
                    label="Category"
                    error={fieldState.error?.message}
                    {...field}
                  />
                )}
              />

              <Controller
                name="subCategory"
                control={control}
                render={({field, fieldState}) => (
                  <SubCategorySelect
                    label="Sub-category"
                    categoryId={currentCategory?.id ?? ''}
                    error={fieldState.error?.message}
                    {...field}
                  />
                )}
              />

              <Controller
                name="geos"
                control={control}
                render={({field}) => {
                  return (
                    <ObjectSelect<GeoFilterOption>
                      label="GEO tags"
                      multiple
                      options={geoOptions}
                      labelProp="name"
                      valueProp="id"
                      value={field.value ?? []}
                      onChange={val => field.onChange(val ?? [])}
                      renderOption={opt => (
                        <MenuItem key={opt.id} value={opt.id}>
                          <Box display="flex" alignItems="center" gap={1}>
                            {opt.logo_url && (
                              <img
                                src={opt.logo_url}
                                alt=""
                                width="auto"
                                height={20}
                                style={{flexShrink: 0}}
                              />
                            )}
                            <Typography>{opt.name || opt.id}</Typography>
                          </Box>
                        </MenuItem>
                      )}
                      renderValue={selected => (
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                          {(selected as string[]).map(id => {
                            const opt = geoOptions.find(o => o.id === id);
                            return (
                              <Chip
                                key={id}
                                size="small"
                                label={opt ? opt.name || opt.id : id}
                              />
                            );
                          })}
                        </Box>
                      )}
                    />
                  );
                }}
              />
            </Stack>
          </Grid>
        </Grid>

        <FormRow mt={2} mb={6}>
          <Box display="flex" gap={4} alignItems="flex-start" flexWrap="wrap">
            <Stack spacing={1} width={160}>
              <ImageUploadField<FormValues>
                fieldName="logoFile"
                label="Avatar"
                acceptedFileTypes={imageFileTypes}
                existingFileUrl={undefined}
                width={160}
                height={160}
                alt="Topic avatar"
              />
            </Stack>

            <Stack spacing={1} width={420}>
              <ImageUploadField<FormValues>
                fieldName="bannerFile"
                label="Banner"
                acceptedFileTypes={imageFileTypes}
                existingFileUrl={undefined}
                width="100%"
                height={160}
                imageFit="cover"
                alt="Topic banner"
              />
            </Stack>
          </Box>
        </FormRow>

        <FormRow width={420} mt={2}>
          <Button
            type="button"
            variant="outlined"
            onClick={onCancel}
            sx={{flex: 1, border: '0.4px solid #CBCDD8 !important'}}
          >
            Back
          </Button>

          <Button
            type="submit"
            sx={{flex: 2}}
            disabled={isUploadingFiles || isCreatingTopic}
          >
            Create topic
          </Button>
        </FormRow>
      </FormProvider>
    </form>
  );
};
